import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Form, Button} from 'react-bootstrap';
import {formatPrice, genericMessageHandler, Icon} from 'shared/helpers';
import {addCouponToJob, deleteCoupon} from 'service/JobService';
import {useNotificationContext, useJobContext, useAppContext} from 'contexts';
import {isEmpty, trim} from 'lodash';
import styled from 'styled-components';
import {
    invalidateJob,
    invalidateJobCost,
    useGetJobCostQuery,
} from 'components/customer/Job/store/jobApi';
import {useAppDispatch} from 'store/customer';
import {DateTime, DateType} from '@cabinetsbycomputer/datetime';
import {PartialJob} from 'shared/types/PartialJob';
import {invalidateUser} from 'components/customer/Auth/store/userSlice';
import {AxiosResponseOrError} from 'service/Axios';

const ExpiredCouponText = styled.div`
    color: red;
    font-size: 11px;
`;

export const Coupon = (): JSX.Element => {
    const {userProfile} = useAppContext();
    const [couponCode, setCouponCode] = useState('');
    const [couponField, setCouponField] = useState('');
    const [loader, setLoader] = useState(false);
    const {notify} = useNotificationContext();
    const {job, showDialog} = useJobContext() as PartialJob;
    const [expiredCoupon, setExpiredCoupon] = useState(false);
    const dispatch = useAppDispatch();
    const {data: cost} = useGetJobCostQuery(
        {jobId: job.id},
        {
            skip: typeof job?.id === 'undefined',
        }
    );

    const handleCouponApiFailure = useCallback(
        (responseOrError: AxiosResponseOrError) => {
            setCouponCode('');
            setCouponField('');

            // Refetch user config on feature disabled errors
            if (responseOrError?.status === 403) {
                dispatch(invalidateUser());
            }

            const message: string =
                responseOrError?.data?.detail ?? responseOrError?.data?.error;
            if (typeof message === 'string') {
                genericMessageHandler(notify, [message]);
                return;
            }

            genericMessageHandler(
                notify,
                'Something went wrong while updating the coupon. ' +
                    'Please try again and contact support if the problem persists.'
            );
        },
        [setCouponCode, setCouponField, notify]
    );

    const deleteCouponFromJob = useCallback(async () => {
        setLoader(true);
        try {
            await deleteCoupon(job.id);
            setCouponCode('');
            setCouponField('');
            setExpiredCoupon(false);
            genericMessageHandler(
                notify,
                {message: 'Coupon deleted successfully!'},
                'success'
            );
            dispatch(invalidateJob());
            dispatch(invalidateJobCost());
        } catch (error) {
            handleCouponApiFailure(error as AxiosResponseOrError);
        } finally {
            setLoader(false);
        }
    }, [job, notify]);

    const deletePrompt = useCallback(() => {
        showDialog({
            title: 'Delete Coupon',
            message: 'Are you sure you want to delete this coupon?',
            yes: deleteCouponFromJob,
        });
    }, [deleteCouponFromJob, showDialog]);

    const addCoupon = useCallback(async () => {
        if (isEmpty(trim(couponField))) {
            genericMessageHandler(notify, {
                message: `Coupon code can't be empty`,
            });
            return;
        }
        setLoader(true);
        try {
            await addCouponToJob(job.id, couponField);
            genericMessageHandler(
                notify,
                {message: 'Coupon added successfully!'},
                'success'
            );
            dispatch(invalidateJob());
            dispatch(invalidateJobCost());
        } catch (error) {
            handleCouponApiFailure(error as AxiosResponseOrError);
        } finally {
            setLoader(false);
        }
    }, [couponField, notify, addCouponToJob, job]);

    const handleAddCoupon = useCallback(() => {
        void addCoupon();
    }, [addCoupon]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setCouponField(e.target.value);
        },
        [setCouponField]
    );

    useEffect(() => {
        if (cost && cost.couponDetails && !cost.couponDetails.invalid) {
            setCouponCode(cost.couponDetails.coupon_code);
            // check if coupon expired

            if (
                !isEmpty(cost.couponDetails.expiry_date) &&
                DateTime.parse(
                    cost.couponDetails.expiry_date,
                    DateType.ISO
                ).isBefore(new Date())
            ) {
                setExpiredCoupon(true);
            }
        }

        return () => {
            setCouponCode('');
            setCouponField('');
        };
    }, [job, cost]);

    const couponIsApplied = useMemo(() => {
        return couponCode && couponCode.length;
    }, [couponCode]);

    if (!userProfile.manufacturerCouponsEnabled && !couponIsApplied) {
        return null;
    }

    return (
        <Form.Group
            controlId="coupon_"
            style={{display: 'flex', width: '100%', marginBottom: 0}}>
            <div style={{flex: 1, margin: '0px'}}>
                <Form.Control
                    type="text"
                    name="coupon"
                    value={
                        couponCode && cost
                            ? `${couponCode} ${'    '}-${formatPrice(
                                  cost.coupon,
                                  cost
                              )}`
                            : couponField
                    }
                    placeholder="Coupon Code"
                    disabled={
                        !userProfile.manufacturerCouponsEnabled ||
                        couponIsApplied ||
                        job.status == 5
                    }
                    style={{fontSize: '10px'}}
                    onChange={handleChange}
                    title={
                        userProfile.manufacturerCouponsEnabled
                            ? 'Apply a coupon to your job'
                            : 'Remove coupon'
                    }
                />
                {expiredCoupon ? (
                    <ExpiredCouponText>Coupon expired</ExpiredCouponText>
                ) : null}

                {job.couponDetails && job.couponDetails.invalid ? (
                    <ExpiredCouponText>Invalid Coupon.</ExpiredCouponText>
                ) : null}
            </div>
            <div>
                {couponCode && job.status != 5 ? (
                    <Button
                        variant="link"
                        className="mobile-lone-button"
                        style={{
                            margin: '0 2px',
                            padding: 0,
                        }}
                        onClick={deletePrompt}>
                        {loader ? (
                            <Icon
                                iconName="tail-spin.svg"
                                style={{width: '20px', height: '20px'}}
                            />
                        ) : (
                            <Icon
                                iconName="Options-Delete.svg"
                                style={{width: '20px', height: '20px'}}
                            />
                        )}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={handleAddCoupon}
                        className="button-blue"
                        style={{
                            borderRadius: '5px',
                            margin: '0 0 0 26px',
                            fontSize: '11px',
                            display: isEmpty(trim(couponField))
                                ? 'none'
                                : 'block',
                        }}>
                        {loader ? (
                            <Icon
                                iconName="tail-spin.svg"
                                style={{width: '20px', height: '20px'}}
                            />
                        ) : (
                            <span>Apply</span>
                        )}
                    </Button>
                )}
            </div>
        </Form.Group>
    );
};
